import { getDefaultInputValues } from "@/features/branding";
import { CollectionAsset } from "@/features/collection_home";
import { AdaptationListAsset } from "@/features/collection_home/types";
import { MapDisplayPoint } from "@/features/maps/types";
import { Hazard, HazardPlusTotal, Scenario, ValuesByYear, Year } from "@/features/pe_aldd";
import { ScenarioMetricResult } from "@/features/pe_aldd/schema";
import { PeaSummaryResponse, PeaSummaryRow, YearSeries } from "@/store/services/facades/types/pea";
import { years } from "@/types/year";

const isNonNullEntry = (entry: [string, number | null]): entry is [string, number] => {
  return entry[1] !== null;
};

const transformValuesByYearToYearSeries = (values: ValuesByYear): YearSeries => {
  return Object.fromEntries(
    Object.entries(values)
      .filter(isNonNullEntry)
      .map(([year, value]) => [Number(year) as Year, value])
  ) as YearSeries;
};

const calculateTtip = (damageValues: ValuesByYear, replacementCost: number): YearSeries => {
  return Object.fromEntries(
    Object.entries(damageValues)
      .filter(isNonNullEntry)
      .map(([year, value]) => [Number(year) as Year, value * replacementCost])
  ) as YearSeries;
};

const EMPTY_VALUES_BY_YEAR = Object.fromEntries(years.map((y) => [y, null] as const)) as ValuesByYear;

const extractReplacementCost = (point?: MapDisplayPoint, regionType?: string): number | undefined => {
  if (!point?.asset?.data) return undefined;

  const assetData = point.asset.data;
  const archetype = assetData?.archetype;
  const defaultValues = getDefaultInputValues(archetype, regionType);
  return assetData?.financial?.replacement_cost ?? defaultValues.replacementCost;
};

const extractMarketValue = (point?: MapDisplayPoint, regionType?: string): number | undefined => {
  if (!point?.asset?.data) return undefined;

  const assetData = point.asset.data;
  const defaultValues = getDefaultInputValues(assetData?.archetype, regionType);
  return assetData?.financial?.market_value ?? defaultValues.marketValue;
};

const createEmptyAbcRatingMetrics = () => ({
  a_rating_per: {} as YearSeries,
  b_rating_per: {} as YearSeries,
  c_rating_per: {} as YearSeries,
  a_rating_total: {} as YearSeries,
  b_rating_total: {} as YearSeries,
  c_rating_total: {} as YearSeries,
});

const createBaseSummaryRow = (
  scenario: Scenario,
  hazard: Hazard | "total",
  replacementCost: number | undefined,
  marketValue: number | undefined
): Pick<
  PeaSummaryRow,
  | "job_id"
  | "climate_scenario"
  | "hazard"
  | "abc_rating_metrics"
  | "cav"
  | "count"
  | "total_market_value"
  | "average_market_value"
  | "total_replacement_cost"
  | "average_replacement_cost"
> => ({
  job_id: "transformed",
  climate_scenario: scenario,
  hazard,
  abc_rating_metrics: createEmptyAbcRatingMetrics(),
  cav: null,
  count: 1,
  total_market_value: marketValue ?? null,
  average_market_value: marketValue ?? null,
  total_replacement_cost: replacementCost ?? null,
  average_replacement_cost: replacementCost ?? null,
});

export const transformResultToSummary = (
  point: MapDisplayPoint,
  asset: CollectionAsset,
  regionType?: string
): PeaSummaryResponse => {
  const result = asset.result;
  const replacementCost = extractReplacementCost(point, regionType);
  const marketValue = extractMarketValue(point, regionType);
  const summaryRows: PeaSummaryRow[] = [];

  Object.entries(result.results).forEach(([scenarioKey, scenarioData]) => {
    const scenario = scenarioKey as Scenario;

    // Process 'total' hazard
    // Note: failure and productivity_loss might be undefined for lightweight results
    const totalRow: PeaSummaryRow = {
      ...createBaseSummaryRow(scenario, "total", replacementCost, marketValue),
      avg_damage: transformValuesByYearToYearSeries(scenarioData.damage.total),
      sum_damage: transformValuesByYearToYearSeries(scenarioData.damage.total),
      avg_failure: scenarioData.failure?.total
        ? transformValuesByYearToYearSeries(scenarioData.failure.total)
        : ({} as YearSeries),
      avg_damage_mtd: transformValuesByYearToYearSeries(scenarioData.damage.total),
      avg_failure_mtd: scenarioData.failure?.total
        ? transformValuesByYearToYearSeries(scenarioData.failure.total)
        : ({} as YearSeries),
      productivity_loss: scenarioData.productivity_loss?.total
        ? transformValuesByYearToYearSeries(scenarioData.productivity_loss.total)
        : ({} as YearSeries),
      ttip: replacementCost !== undefined ? calculateTtip(scenarioData.damage.total, replacementCost) : null,
    };
    summaryRows.push(totalRow);

    // Process individual hazards
    Object.keys(scenarioData.damage.by_hazard).forEach((hazardKey) => {
      const hazard = hazardKey as Hazard;

      const mvar = scenarioData.damage.by_hazard[hazard];
      const fp = scenarioData.failure?.by_hazard?.[hazard];
      const pl = scenarioData.productivity_loss?.by_hazard?.[hazard];

      if (!mvar && !fp && !pl) return;

      const row: PeaSummaryRow = {
        ...createBaseSummaryRow(scenario, hazard, replacementCost, marketValue),
        avg_damage: transformValuesByYearToYearSeries(mvar),
        sum_damage: transformValuesByYearToYearSeries(mvar),
        avg_failure: transformValuesByYearToYearSeries(fp ?? EMPTY_VALUES_BY_YEAR),
        avg_damage_mtd: transformValuesByYearToYearSeries(mvar),
        avg_failure_mtd: transformValuesByYearToYearSeries(fp ?? EMPTY_VALUES_BY_YEAR),
        productivity_loss: transformValuesByYearToYearSeries(pl ?? EMPTY_VALUES_BY_YEAR),
        ttip: replacementCost !== undefined && mvar ? calculateTtip(mvar, replacementCost) : null,
      };
      summaryRows.push(row);
    });
  });

  return summaryRows;
};

/**
 * Lightweight extractor for metrics needed by a specific scenario/hazard/year in adaptation.
 * works for single point with single analysis only
 * TODO: change for multi action pathways
 */
export type PointMetrics = { id: string } & Partial<
  Record<"avg_damage_mtd" | "avg_failure_mtd" | "productivity_loss" | "ttip", number | null>
>;

export type LightweightPointAdaptation = {
  uuid: string;
  data: CollectionAsset["data"];
  result: AdaptationListAsset["result"];
};

export const extractPointMetricsForSingleResult = (
  asset: LightweightPointAdaptation,
  scenario: Scenario,
  hazard: HazardPlusTotal,
  year: Year,
  regionType?: string
): PointMetrics => {
  const replacementCost = extractReplacementCost({ asset } as unknown as MapDisplayPoint, regionType);
  const scenarioData = asset.result.results[scenario];

  const valueFromYear = (scenarioMetricResult?: ScenarioMetricResult): number | null => {
    if (!scenarioMetricResult) return null;
    const values = hazard === "total" ? scenarioMetricResult.total : scenarioMetricResult.by_hazard?.[hazard];
    return values?.[year] ?? null;
  };

  const damage = valueFromYear(scenarioData?.damage);
  const failure = valueFromYear(scenarioData?.failure);
  const productivityLoss = valueFromYear(scenarioData?.productivity_loss);

  return {
    id: asset.uuid,
    avg_damage_mtd: damage != null ? damage * 100 : null,
    avg_failure_mtd: failure != null ? failure * 100 : null,
    productivity_loss: productivityLoss != null ? productivityLoss * 100 : null,
    ttip: replacementCost !== undefined && damage != null ? damage * replacementCost : null,
  };
};
